import { useLocation, Link } from 'react-router-dom';
import { useRoleLabel } from '../hooks/useRoleLabel.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import LanguageSwitcher from './LanguageSwitcher.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useDashboardTheme } from '../hooks/useDashboardTheme.js';
import { useState } from 'react';
import { Sun, Moon, LogOut, LayoutDashboard, CreditCard, Link2, BarChart3, Users, Building2, Wallet, HelpCircle, Landmark, Settings, Contact, BrainCircuit, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import Brand from './Brand';
interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  superadminOnly?: boolean;
  memberOnly?: boolean;
  allowedRoles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/',              labelKey: 'nav.dashboard',     icon: LayoutDashboard, allowedRoles: ['superadmin', 'owner', 'admin', 'department_lead'] },
  { to: '/cards',         labelKey: 'nav.cards',         icon: CreditCard,      allowedRoles: ['superadmin', 'owner', 'admin', 'department_lead'] },
  { to: '/links',         labelKey: 'nav.links',         icon: Link2 },
  { to: '/analytics',     labelKey: 'nav.analytics',     icon: BarChart3 },
  { to: '/leads',         labelKey: 'nav.leads',         icon: Contact },
  { to: '/users',         labelKey: 'nav.users',         icon: Users },
  { to: '/departments',   labelKey: 'nav.departments',   icon: Building2,       allowedRoles: ['superadmin', 'owner', 'admin'] },
  { to: '/billing',       labelKey: 'nav.billing',       icon: Wallet,          allowedRoles: ['superadmin', 'owner', 'admin'] },
  { to: '/help',          labelKey: 'nav.help',          icon: HelpCircle },
  { to: '/organisations', labelKey: 'nav.organisations', icon: Landmark,        superadminOnly: true },
  { to: '/ocr-learning',  labelKey: 'nav.ocrLearning',   icon: BrainCircuit,    superadminOnly: true },
  { to: '/security',      labelKey: 'nav.security',      icon: ShieldCheck },
  { to: '/settings',      labelKey: 'nav.settings',      icon: Settings,        allowedRoles: ['superadmin', 'owner', 'admin', 'department_lead'] },
];

interface Props {
  children: React.ReactNode;
  title?: string;
}

export default function Layout({ children, title }: Props) {
  const { t } = useAppTranslation();
  const roleLabel = useRoleLabel();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isDark, toggle } = useDashboardTheme();

  const currentItem = NAV_ITEMS.slice().reverse().find((item) =>
    pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to))
  ) ?? NAV_ITEMS[0]!;

  return (
    <div className="flex min-h-screen bg-canvas text-ink">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 flex w-60 flex-col bg-panel border-r border-line z-40 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-16 items-center px-5 border-b border-line">
          <Brand />
        </div>

        <nav className="flex-1 space-y-0.5 px-3 py-4 overflow-y-auto">
          {NAV_ITEMS.filter((item) => {
            if (item.superadminOnly && user?.role !== 'superadmin') return false;
            if (item.allowedRoles && user?.role && !item.allowedRoles.includes(user.role)) return false;
            return true;
          }).map((item) => {
            const isActive = item.to === '/'
              ? pathname === '/'
              : pathname === item.to || pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={`relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-raised text-ink font-medium'
                    : item.superadminOnly
                      ? 'text-warning-600/70 dark:text-warning-400/50 hover:bg-warning-50 dark:hover:bg-warning-500/5 hover:text-warning-700 dark:hover:text-warning-300/70'
                      : 'text-ink-muted hover:bg-raised hover:text-ink'
                }`}
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden="true" />
                <span>{t(item.labelKey)}</span>
                {isActive && (
                  <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-accent-500" aria-hidden="true" />
                )}
              </Link>
            );
          })}
        </nav>

        {user && (
          <div className="border-t border-line px-4 py-4 space-y-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-600/30 text-sm font-bold text-accent-700 dark:text-accent-300">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-ink truncate">{user.email}</p>
                <p className="text-xs text-ink-faint">{roleLabel(user.role)}</p>
              </div>
            </div>
            <button
              onClick={() => logout()}
              className="w-full rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-line-strong hover:text-ink transition-all text-left"
            >
              <LogOut className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" aria-hidden="true" />{t('nav.logout')}
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-60">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-line bg-canvas/80 backdrop-blur-sm px-4 sm:px-8 gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-raised transition-colors text-ink-muted"
            aria-label={t('nav.openMenu', 'Menü öffnen')}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="min-w-0 truncate font-display text-lg font-semibold tracking-tight text-ink sm:text-xl">
            {title ?? t(currentItem.labelKey)}
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggle}
              className="p-2 rounded-lg text-ink-muted hover:bg-raised transition-colors"
              aria-label={isDark ? t('nav.lightMode', 'Hellmodus') : t('nav.darkMode', 'Dunkelmodus')}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSwitcher />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 sm:px-8 py-5 lg:py-6">
          {children}
        </main>
      </div>
    </div>
  );
}
